import { captureException } from '@sentry/react';
import { queryOptions } from '@tanstack/react-query';
import z from 'zod';

import { authorizedAxiosInstance } from '@/clients';
import type { FMPCompany, FMPFundAllocation, FMPFundHolding } from '@/types';

export const FUND_HOLDINGS_PAGE_SIZE = 25;

const nullableString = z
    .string()
    .trim()
    .nullish()
    .transform((value) => value || undefined);
const nullableNumber = z
    .union([z.number(), z.string()])
    .nullish()
    .transform((value) => {
        if (value === null || value === undefined || value === '') {
            return undefined;
        }

        const parsed = typeof value === 'number' ? value : Number(value.replace('%', ''));

        return Number.isFinite(parsed) ? parsed : undefined;
    });

const fundHoldingSchema = z
    .object({
        asset: nullableString,
        companyName: nullableString,
        country: nullableString,
        cusip: nullableString,
        exchange: nullableString,
        isin: nullableString,
        marketValue: nullableNumber,
        name: nullableString,
        sector: nullableString,
        sharesNumber: nullableNumber,
        symbol: nullableString,
        ticker: nullableString,
        weight: nullableNumber,
        weightPercentage: nullableNumber,
    })
    .passthrough();

const fundHoldingsPageSchema = z.object({
    data: z.array(fundHoldingSchema).default([]),
    pagination: z.object({
        limit: z.number().int().positive(),
        nextOffset: z.number().int().nonnegative().nullable(),
        offset: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});

const fundAllocationSchema = z
    .object({
        country: nullableString,
        sector: nullableString,
        weight: nullableNumber,
        weightPercentage: nullableNumber,
    })
    .passthrough();

export interface FundHoldingsPage {
    data: FMPFundHolding[];
    pagination: {
        limit: number;
        nextOffset: number | null;
        offset: number;
        total: number;
    };
}

const normalizeSymbol = (symbol: string) => symbol.trim().toLocaleUpperCase();

const fetchFundHoldingsPage = async (symbol: string, offset: number, signal: AbortSignal) => {
    const response = await authorizedAxiosInstance.get<unknown>(
        `/api/vendors/fmp/funds/${encodeURIComponent(normalizeSymbol(symbol))}/holdings`,
        {
            params: { limit: FUND_HOLDINGS_PAGE_SIZE, offset },
            signal,
        },
    );

    return fundHoldingsPageSchema.parse(response.data) as FundHoldingsPage;
};

const fetchFundAllocations = async (
    symbol: string,
    kind: 'country-weightings' | 'sector-weightings',
    signal: AbortSignal,
) => {
    const response = await authorizedAxiosInstance.get<unknown>(
        `/api/vendors/fmp/funds/${encodeURIComponent(normalizeSymbol(symbol))}/${kind}`,
        { signal },
    );
    const payload = Array.isArray(response.data)
        ? response.data
        : response.data && typeof response.data === 'object'
          ? ((response.data as Record<string, unknown>).data ?? [])
          : [];

    return z.array(fundAllocationSchema).parse(payload) as FMPFundAllocation[];
};

const fetchHoldingProfiles = async (symbols: string[], signal: AbortSignal) => {
    if (!symbols.length) {
        return [];
    }

    const uniqueSymbols = Array.from(new Set(symbols.map(normalizeSymbol)));
    const batches = Array.from({ length: Math.ceil(uniqueSymbols.length / 20) }, (_value, index) =>
        uniqueSymbols.slice(index * 20, (index + 1) * 20),
    );
    const responses = [];

    // Keep provider-backed enrichment batches sequential so scrolling cannot create a burst
    // of concurrent profile fan-out requests at the API boundary.
    for (const batch of batches) {
        responses.push(
            await authorizedAxiosInstance.post<unknown>(
                '/api/vendors/fmp/profiles/enriched',
                { symbols: batch },
                { signal },
            ),
        );
    }

    return responses.flatMap((response) => (Array.isArray(response.data) ? response.data : [])) as FMPCompany[];
};

export const fundHoldingsQueryKeys = {
    allocations: (symbol: string, kind: 'country-weightings' | 'sector-weightings') =>
        ['search', 'funds', normalizeSymbol(symbol), 'allocations', kind] as const,
    holdings: (symbol: string) => ['search', 'funds', normalizeSymbol(symbol), 'holdings'] as const,
    profiles: (symbols: string[]) =>
        ['search', 'funds', 'holding-profiles', ...Array.from(new Set(symbols.map(normalizeSymbol))).sort()] as const,
};

export const fundHoldingsQueryOptions = {
    allocations: (symbol: string, kind: 'country-weightings' | 'sector-weightings') =>
        queryOptions({
            queryFn: async ({ signal }) => {
                try {
                    return await fetchFundAllocations(symbol, kind, signal);
                } catch (error) {
                    if (!signal.aborted) {
                        captureException(error);
                    }

                    throw error;
                }
            },
            queryKey: fundHoldingsQueryKeys.allocations(symbol, kind),
            staleTime: 15 * 60 * 1_000,
        }),
    page: (symbol: string) => ({
        queryFn: async ({ pageParam = 0, signal }: { pageParam?: number; signal: AbortSignal }) => {
            try {
                return await fetchFundHoldingsPage(symbol, pageParam, signal);
            } catch (error) {
                if (!signal.aborted) {
                    captureException(error);
                }

                throw error;
            }
        },
        queryKey: fundHoldingsQueryKeys.holdings(symbol),
        staleTime: 15 * 60 * 1_000,
    }),
    profiles: (symbols: string[]) =>
        queryOptions({
            queryFn: ({ signal }) => fetchHoldingProfiles(symbols, signal),
            queryKey: fundHoldingsQueryKeys.profiles(symbols),
            staleTime: 60 * 60 * 1_000,
        }),
};
